import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import OTPInput from 'react-otp-input';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';

import Button from '../../../../components/atoms/button';
import { LOGIN_STEPS, OTP_EXPIRES_IN, OTP_LENGTH } from '../../../../constants/login';
import { SEND_OTP_EMAIL, VERIFY_LOGIN_OTP } from '../../../../apis/user';

const calculateTimePassedInPercentage = (elapsedTime) => ((OTP_EXPIRES_IN - elapsedTime) * 100) / OTP_EXPIRES_IN;

const VerifyOTPViaEmail = (props) => {
  // init
  const { email, formattedTimeLeft, timeLeft, isOtpExpired, setLoginStep, setIsLoading, setToast, startTimer, handleVerifyCodeSuccess } = props;

  // mutations / queries
  const [generateLoginOTP] = useMutation(SEND_OTP_EMAIL);
  const [verifyLoginOtp] = useMutation(VERIFY_LOGIN_OTP);

  // states
  const [otp, setOtp] = useState('');
  const [isOtpValid, setIsOtpValid] = useState(true);

  const handleGoBack = () => {
    setLoginStep(LOGIN_STEPS.ENTER_EMAIL);
  };

  const handleVerifyCode = async () => {
    if (otp.length !== OTP_LENGTH) {
      setIsOtpValid(false);
      return;
    }

    try {
      setIsLoading(true);
      setIsOtpValid(true);
      const { data, loading, error } = await verifyLoginOtp({ variables: { email, code: otp } });
      if (data && !loading && !error) {
        handleVerifyCodeSuccess(data);
      }
    } catch (e) {
      if (e.message === 'OTP not verified') {
        setIsOtpValid(false);
      } else {
        setToast((prevstate) => ({
          ...prevstate,
          message: 'Something went wrong!',
          open: true,
          type: 'error',
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      const { data, loading, error } = await generateLoginOTP({ variables: { email } });
      if (data && !loading && !error) {
        startTimer();
        setLoginStep(LOGIN_STEPS.VERIFY_OTP);
      }
    } catch (error) {
      setToast((prevstate) => ({
        ...prevstate,
        message: 'Something went wrong!',
        open: true,
        type: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div>
        <h4 className="login-title">We sent a verification code to your email.</h4>
        <h4 className="login-subtitle">Enter the 6 digit code you received at {email} below to proceed.</h4>
      </div>
      <div className="border-card-input-container">
        <span className="border-card-label">Enter verification code</span>
        <OTPInput
          inputType="number"
          shouldAutoFocus
          containerStyle={{
            gap: 10,
          }}
          inputStyle={{
            border: `1px solid ${isOtpValid ? '#D0D5DD' : '#DD0202'}`,
            borderRadius: 6,
            fontSize: 24,
            fontWeight: 600,
            width: '100%',
            height: 50,
            padding: 10,
          }}
          value={otp}
          numInputs={OTP_LENGTH}
          onChange={setOtp}
          renderInput={(rest) => <input {...rest} />}
        />
        {!isOtpValid && <span className="input-error">The verification code is not valid</span>}
      </div>
      <Button fullWidth onClick={handleVerifyCode} primary><strong>Verify Code</strong></Button>
      <div className="resend-code-container">
        <span>Didn&apos;t receive code?</span>
        <div className="code-expiry-timer">
          <CircularProgress
            className="timer-placeholder"
            color="primary"
            thickness={6}
            size={20}
            variant="determinate"
            value={100}
          />
          <CircularProgress
            thickness={6}
            style={{ color: '#d0d5dd' }}
            size={20}
            variant="determinate"
            value={calculateTimePassedInPercentage(timeLeft)}
          />
          <span>{formattedTimeLeft}</span>
        </div>
        <button disabled={!isOtpExpired} onClick={handleResendCode} type="button" className="link-button" href="#">Resend Code</button>
      </div>
      <button type="button" className="link-button" onClick={handleGoBack}>Go Back</button>
    </>
  );
};

VerifyOTPViaEmail.propTypes = {
  email: PropTypes.string.isRequired,
  formattedTimeLeft: PropTypes.string.isRequired,
  timeLeft: PropTypes.number.isRequired,
  startTimer: PropTypes.func.isRequired,
  setLoginStep: PropTypes.func.isRequired,
  isOtpExpired: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
  handleVerifyCodeSuccess: PropTypes.func.isRequired,
};

export default VerifyOTPViaEmail;
